import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ChevronLeft,
    Loader2,
    ReceiptText,
    RefreshCcw,
    Wallet,
    Calendar as CalendarIcon,
    ArrowUpRight,
    Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    fetchAllBankAccounts,
    fetchExpenseTransactionsFromFundingAccount,
    currencyCodeToSymbolMap
} from '../apiHelper';
import { useToast } from '../hooks/useToast';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import TrialModal from '../components/ui/TrialModal';

const ExpenseReport = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    // State
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
    const [reportData, setReportData] = useState([]);

    // Loading States
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    // Load Bank Accounts on mount
    useEffect(() => {
        const loadBankAccounts = async () => {
            setLoadingBankAccounts(true);
            try {
                const accounts = await fetchAllBankAccounts();
                const filteredAndSorted = accounts
                    .filter(a => a.is_active && a.is_leaf)
                    .map(a => ({
                        label: `${a.name} (${a.account_code})`,
                        value: a.account_id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setBankAccountOptions(filteredAndSorted);
            } catch (err) {
                showToast('Failed to load bank accounts', 'error');
                setBankAccountOptions([]);
            } finally {
                setLoadingBankAccounts(false);
            }
        };
        loadBankAccounts();
    }, [showToast]);

    // Fetch Transactions logic
    const fetchReport = useCallback(async () => {
        if (!selectedAccountId || !targetDate) return;

        setLoadingReport(true);
        try {
            const payload = {
                account_id: selectedAccountId,
                target_date: targetDate
            };
            const data = await fetchExpenseTransactionsFromFundingAccount(payload);
            setReportData(data);
        } catch (err) {
            showToast('Error fetching expense transactions', 'error');
            setReportData([]);
        } finally {
            setLoadingReport(false);
        }
    }, [selectedAccountId, targetDate, showToast]);

    // Trigger fetch on selection/date change
    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Calculations
    const totalExpenses = useMemo(() => {
        return reportData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    }, [reportData]);

    const formatCurrency = (amount) => {
        const symbol = currencyCodeToSymbolMap['NGN'] || '₦'; // Defaulting to NGN based on your logs
        return `${symbol}${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount)}`;
    };

    return (
        <div className="p-4 md:p-8 bg-[#FBFBFE] min-h-screen">
            <div className="max-w-5xl mx-auto">

                {/* Header Section */}
                <header className="mb-8">
                    <Button
                        onClick={() => navigate(-1)}
                        variant="ghost"
                        className="p-0 h-auto mb-4 text-slate-400 hover:text-indigo-600 flex items-center transition-colors"
                    >
                        <ChevronLeft size={18} />
                        <span className="text-xs uppercase tracking-widest font-bold ml-1">Back</span>
                    </Button>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Daily Expense Report</h1>
                            <p className="text-slate-500 font-medium mt-1">Track outflows from your funding accounts</p>
                        </div>
                        <div className="bg-indigo-600 px-6 py-3 rounded-2xl shadow-lg shadow-indigo-200 flex flex-col items-end">
                            <span className="text-indigo-100 text-[10px] font-bold uppercase tracking-widest">Total for Day</span>
                            <span className="text-white text-2xl font-black tracking-tight">
                                {formatCurrency(totalExpenses)}
                            </span>
                        </div>
                    </div>
                </header>

                {/* Filters Section */}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8 bg-white p-5 rounded-2xl shadow-sm border border-slate-100 items-end">
                    <div className="md:col-span-7">
                        <SearchableSelectBox
                            label="Funding Account (Bank/Cash)"
                            options={bankAccountOptions}
                            value={selectedAccountId}
                            onChange={(e) => setSelectedAccountId(e.target.value)}
                            loading={loadingBankAccounts}
                            placeholder="Select account to view expenses..."
                            containerClassName="mb-0"
                        />
                    </div>
                    <div className="md:col-span-4">
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center">
                            <CalendarIcon size={12} className="mr-1" /> Target Date
                        </label>
                        <input
                            type="date"
                            value={targetDate}
                            onChange={(e) => setTargetDate(e.target.value)}
                            className="w-full mb-4 bg-slate-50 border-none rounded-xl p-3 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                        />
                    </div>
                    <div className="md:col-span-1 flex justify-end mb-4">
                        <Button
                            onClick={fetchReport}
                            disabled={loadingReport || !selectedAccountId}
                            className="w-full md:w-[48px] h-[48px] p-0 flex items-center justify-center rounded-xl bg-slate-900 hover:bg-indigo-600 text-white transition-all shadow-md"
                        >
                            {loadingReport ? <Loader2 className="animate-spin" size={20} /> : <RefreshCcw size={20} />}
                        </Button>
                    </div>
                </div>

                {/* Report Content */}
                <Card className="border-none shadow-2xl shadow-slate-200/50 rounded-3xl overflow-hidden">
                    {loadingReport ? (
                        <div className="py-20 flex flex-col items-center justify-center">
                            <Loader2 className="animate-spin text-indigo-500 mb-4" size={40} />
                            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.2em]">Processing Ledger</p>
                        </div>
                    ) : reportData.length === 0 ? (
                        <div className="py-20 flex flex-col items-center justify-center text-center px-6">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                <Search className="text-slate-200" size={32} />
                            </div>
                            <h3 className="text-slate-900 font-bold text-lg">No expenses found</h3>
                            <p className="text-slate-400 text-sm max-w-xs mx-auto mt-1">Select a funding account or check a different date to see transactions.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100">
                                        <th className="px-6 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">Expense Account</th>
                                        <th className="px-6 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">Details</th>
                                        <th className="px-6 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {reportData.map((item, idx) => (
                                        <tr key={idx} className="group hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-5">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                                                        <ArrowUpRight size={16} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-800 uppercase tracking-tight">{item.account}</p>
                                                        <p className="text-[10px] text-slate-400 font-medium">REF: {item.reference}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <p className="text-xs text-slate-600 font-medium max-w-xs truncate">{item.description}</p>
                                                <p className="text-[10px] text-indigo-400 font-bold mt-1 uppercase italic">By {item.creator}</p>
                                            </td>
                                            <td className="px-6 py-5 text-right">
                                                <span className="text-sm font-black text-slate-900">
                                                    {formatCurrency(item.amount)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default ExpenseReport;